import { ReleaseNoteProduct } from '@features/components/ReleaseNotes';
import { getReleaseNoteApiSource, ReleaseNoteApiSource } from '@lp/pages/admin/releasenotes/hooks';
import { useCreateReleaseNoteMutation, useUpdateReleaseNoteMutation } from '@/queries/release-notes';
import { useCreateOpobffReleaseNoteMutation, useUpdateOpobffReleaseNoteMutation } from '@/queries/opobff';
import {
  useCreateResourceReleaseNoteMutation,
  useUpdateResourceReleaseNoteMutation,
} from '@/queries/resource/release-notes';
import { CreateReleaseNotePayload } from '@/api/apis/release-notes';

/**
 * Payload emitted by the form. Shape matches every backing API — Resource Service
 * requests are distinguished by a `:product` URL path segment, not a payload field.
 */
export type ReleaseNoteFormPayload = {
  version: string;
  releaseDate: string;
  highlights: string[];
  bugFixes: string[];
  platform?: string;
};

type SaveReleaseNoteArgs = {
  payload: ReleaseNoteFormPayload;
  releaseNoteId?: string;
};

/**
 * Routes release-note create/update requests to the API backing the product:
 * - TXC Access  → customerservice
 * - OPO Access  → opobff
 * - Nexus, Live → resourceservice (product scopes the URL, not the payload)
 */
export const useReleaseNoteFormMutations = (product: ReleaseNoteProduct) => {
  const createTxcReleaseNote = useCreateReleaseNoteMutation();
  const updateTxcReleaseNote = useUpdateReleaseNoteMutation();
  const createOpoReleaseNote = useCreateOpobffReleaseNoteMutation();
  const updateOpoReleaseNote = useUpdateOpobffReleaseNoteMutation();
  const createResourceReleaseNote = useCreateResourceReleaseNoteMutation(product);
  const updateResourceReleaseNote = useUpdateResourceReleaseNoteMutation(product);

  const isPending =
    createTxcReleaseNote.isPending ||
    updateTxcReleaseNote.isPending ||
    createOpoReleaseNote.isPending ||
    updateOpoReleaseNote.isPending ||
    createResourceReleaseNote.isPending ||
    updateResourceReleaseNote.isPending;

  const saveReleaseNote = async ({ payload, releaseNoteId }: SaveReleaseNoteArgs) => {
    const source = getReleaseNoteApiSource(product);
    const platformPayload = payload as CreateReleaseNotePayload;

    if (source === ReleaseNoteApiSource.Customer) {
      return releaseNoteId
        ? updateTxcReleaseNote.mutateAsync({ releaseNoteId, payload: platformPayload })
        : createTxcReleaseNote.mutateAsync(platformPayload);
    }

    if (source === ReleaseNoteApiSource.Opo) {
      return releaseNoteId
        ? updateOpoReleaseNote.mutateAsync({ releaseNoteId, payload: platformPayload })
        : createOpoReleaseNote.mutateAsync(platformPayload);
    }

    return releaseNoteId
      ? updateResourceReleaseNote.mutateAsync({ releaseNoteId, payload: platformPayload })
      : createResourceReleaseNote.mutateAsync(platformPayload);
  };

  return { saveReleaseNote, isPending };
};
